<template>
  <div class="console-page analytics-view">
    <a-spin :spinning="confirmLoading" tip="Loading analytics…">
      <h1 class="console-page__title">Analytics</h1>
      <p class="console-page__subtitle">
        Daily engagement: events tied to notifications and scheduled pushes
      </p>

      <a-card :bordered="false" class="console-card console-card--toolbar">
        <div class="analytics-view__toolbar">
          <div class="analytics-view__toolbar-label">
            <a-icon type="calendar" />
            <span>Select date</span>
          </div>
          <a-date-picker
            v-model="selectedDate"
            placeholder="Today (default)"
            class="analytics-view__date-picker"
            @change="dashboardReport"
          />
        </div>
      </a-card>

      <h2 class="console-page__section-title">Events &amp; users</h2>
      <a-card :bordered="false" class="console-card analytics-view__chart-card">
        <template #title>
          Event-wise users
          <span class="analytics-view__card-hint">
            Users per event for the selected day
          </span>
          <a-spin
            v-if="confirmLoading"
            size="small"
            class="analytics-view__inline-spin"
          />
        </template>
        <div v-if="hasChartData(chartSeries)" class="analytics-view__chart-wrap">
          <apexchart
            width="100%"
            height="340"
            :options="chartOptions"
            :series="chartSeries"
            type="bar"
          />
        </div>
        <a-empty
          v-else
          class="analytics-view__chart-empty"
          description="No event data for this date"
        />
      </a-card>

      <h2 class="console-page__section-title">Notifications</h2>
      <a-row :gutter="[24, 24]">
        <a-col :xs="24" :lg="12">
          <a-card :bordered="false" class="console-card analytics-view__chart-card">
            <template #title>
              Schedule notification
              <span class="analytics-view__card-meta">
                {{ notificationTitle }}
              </span>
            </template>
            <div
              v-if="hasChartData(chartSeriesNoti)"
              class="analytics-view__chart-wrap"
            >
              <apexchart
                width="100%"
                height="300"
                :options="chartOptionsNoti"
                :series="chartSeriesNoti"
                type="bar"
              />
            </div>
            <a-empty
              v-else
              class="analytics-view__chart-empty"
              description="No scheduled notification data"
            />
          </a-card>
        </a-col>

        <a-col :xs="24" :lg="12">
          <a-card :bordered="false" class="console-card analytics-view__chart-card">
            <template #title>
              Push notification
              <span class="analytics-view__card-meta">
                {{ notificationTitleWithPush }}
              </span>
            </template>
            <div
              v-if="hasChartData(chartSeriesPushNoti)"
              class="analytics-view__chart-wrap"
            >
              <apexchart
                width="100%"
                height="300"
                :options="chartOptionsPushNoti"
                :series="chartSeriesPushNoti"
                type="bar"
              />
            </div>
            <a-empty
              v-else
              class="analytics-view__chart-empty"
              description="No push notification data"
            />
          </a-card>
        </a-col>
      </a-row>
    </a-spin>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import { sendRequest } from "@/http/axios.method";
import { DatePicker } from "ant-design-vue";

export default {
  components: {
    Apexchart: VueApexCharts,
    "a-date-picker": DatePicker,
  },
  data() {
    return {
      config: {
        Authorization: "Bearer " + this.$store.state.authUser.token,
      },
      dataSource: [],
      dataSourceNoti: [],
      dataSourcePushNoti: [],
      selectedDate: null,
      notificationTitle: "",
      notificationTitleWithPush: "",
      confirmLoading: false,
      chartOptions: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          fontFamily:
            '"Open Sans", Helvetica, Arial, sans-serif',
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: "55%",
          },
        },
        dataLabels: { enabled: false },
        grid: {
          borderColor: "#f0f0f0",
          strokeDashArray: 4,
        },
        xaxis: {
          categories: [],
          labels: {
            show: true,
            rotate: -45,
          },
        },
        yaxis: {
          min: 0,
          labels: {
            show: true,
          },
        },
        colors: ["#03AB77"],
      },
      chartOptionsNoti: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          fontFamily:
            '"Open Sans", Helvetica, Arial, sans-serif',
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: "55%",
          },
        },
        dataLabels: { enabled: false },
        grid: {
          borderColor: "#f0f0f0",
          strokeDashArray: 4,
        },
        xaxis: {
          categories: [],
          labels: {
            show: true,
            rotate: -45,
          },
        },
        yaxis: {
          labels: {
            show: true,
          },
        },
        colors: ["#03AB77"],
      },
      chartOptionsPushNoti: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          fontFamily:
            '"Open Sans", Helvetica, Arial, sans-serif',
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: "55%",
          },
        },
        dataLabels: { enabled: false },
        grid: {
          borderColor: "#f0f0f0",
          strokeDashArray: 4,
        },
        xaxis: {
          categories: [],
          labels: {
            show: true,
            rotate: -45,
          },
        },
        yaxis: {
          labels: {
            show: true,
          },
        },
        colors: ["#722ED1"],
      },
      chartSeries: [],
      chartSeriesNoti: [],
      chartSeriesPushNoti: [],
    };
  },
  mounted() {
    this.dashboardReport();
  },
  methods: {
    hasChartData(seriesList) {
      if (!Array.isArray(seriesList) || seriesList.length === 0) return false;
      return seriesList.some(
        (s) => s && Array.isArray(s.data) && s.data.length > 0
      );
    },
    dashboardReport() {
      this.confirmLoading = true;
      const formattedDate = this.selectedDate
        ? this.selectedDate.format("YYYY-MM-DD")
        : "";

      const form_data = new FormData();
      form_data.append("formattedDate", formattedDate);
      sendRequest("post", "admin/event-wise-user-daily", form_data, this.config)
        .then((response) => {
          this.confirmLoading = false;
          if (response.success) {
            const data = response.data;
            this.dataSource = data;

            this.generateChart(response.data);
            this.dashboardReportNoti();
            this.dashboardReportPushNoti();
          } else {
            if (response.status_code === 401) {
              this.$store.dispatch("logout");
              this.$router.push("/login");
            }
            if (response.status_code === 500) {
              this.revenueReportData = [];
            }
          }
        })
        .catch(() => {
          this.confirmLoading = false;
        })
        .finally(() => {
          this.confirmLoading = false;
        });
    },
    dashboardReportNoti() {
      this.confirmLoading = true;
      const formattedDate = this.selectedDate
        ? this.selectedDate.format("YYYY-MM-DD")
        : "";

      const form_data = new FormData();
      form_data.append("formattedDate", formattedDate);
      sendRequest(
        "post",
        "admin/get-event-wise-user-daily-notification",
        form_data,
        this.config
      )
        .then((response) => {
          this.confirmLoading = false;

          if (response.success) {
            const data = response.data;
            if (data.length > 0) {
              this.notificationTitle = data[0]?.notification_title;
            } else {
              this.notificationTitle = "No data found";
            }
            this.dataSourceNoti = data;

            this.generateChartNoti(response.data);
          } else {
            if (response.status_code === 401) {
              this.$store.dispatch("logout");
              this.$router.push("/login");
            }
            if (response.status_code === 500) {
              this.revenueReportData = [];
            }
          }
        })
        .catch(() => {
          this.confirmLoading = false;
        })
        .finally(() => {
          this.confirmLoading = false;
        });
    },
    dashboardReportPushNoti() {
      this.confirmLoading = true;
      const formattedDate = this.selectedDate
        ? this.selectedDate.format("YYYY-MM-DD")
        : "";

      const form_data = new FormData();
      form_data.append("formattedDate", formattedDate);
      sendRequest(
        "post",
        "admin/get-event-wise-user-daily-push-notification",
        form_data,
        this.config
      )
        .then((response) => {
          this.confirmLoading = false;

          if (response.success) {
            const data = response.data;
            if (data.length > 0) {
              this.notificationTitleWithPush = data[0]?.notification_title;
            } else {
              this.notificationTitleWithPush = "No data found";
            }
            this.dataSourcePushNoti = data;

            this.generateChartPushNoti(response.data);
          } else {
            if (response.status_code === 401) {
              this.$store.dispatch("logout");
              this.$router.push("/login");
            }
            if (response.status_code === 500) {
              this.revenueReportData = [];
            }
          }
        })
        .catch(() => {
          this.confirmLoading = false;
        })
        .finally(() => {
          this.confirmLoading = false;
        });
    },
    generateChart(response) {
      const data = response;
      const tempCategory = [];
      data.map((record) => tempCategory.push(record.event_name));

      this.chartOptions = {
        ...this.chartOptions,
        xaxis: {
          ...this.chartOptions.xaxis,
          categories: tempCategory,
        },
      };

      const counts = data.map((record) => record.count);

      this.chartSeries = [{ name: "Event count", data: counts }];
    },
    generateChartNoti(response) {
      const data = response;

      const tempCategoryNoti = [];
      data.map((record) => tempCategoryNoti.push(record.event_name));

      this.chartOptionsNoti = {
        ...this.chartOptionsNoti,
        xaxis: {
          ...this.chartOptionsNoti.xaxis,
          categories: tempCategoryNoti,
        },
      };

      const counts = data.map((record) => record.count);

      this.chartSeriesNoti = [{ name: "Events", data: counts }];
    },
    generateChartPushNoti(response) {
      const data = response;

      const tempCategoryNoti = [];
      data.map((record) => tempCategoryNoti.push(record.event_name));

      this.chartOptionsPushNoti = {
        ...this.chartOptionsPushNoti,
        xaxis: {
          ...this.chartOptionsPushNoti.xaxis,
          categories: tempCategoryNoti,
        },
      };

      const counts = data.map((record) => record.count);

      this.chartSeriesPushNoti = [{ name: "Events", data: counts }];
    },
  },
};
</script>

<style scoped>
.analytics-view__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 24px;
}

.analytics-view__toolbar-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #434343;
  font-size: 14px;
}

.analytics-view__toolbar-label .anticon {
  color: #03ab77;
  font-size: 16px;
}

.analytics-view__date-picker {
  width: 100%;
  max-width: 280px;
}

.analytics-view__chart-card ::v-deep .ant-card-head-title {
  white-space: normal;
  padding-right: 8px;
  line-height: 1.4;
}

.analytics-view__chart-hint {
  display: block;
  font-weight: 400;
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
}

.analytics-view__inline-spin {
  margin-left: 10px;
  vertical-align: middle;
}

.analytics-view__card-meta {
  display: block;
  font-weight: 500;
  font-size: 13px;
  color: #595959;
  margin-top: 6px;
}

.analytics-view__chart-wrap {
  min-height: 200px;
}

.analytics-view__chart-empty {
  padding: 32px 16px;
  margin: 0;
}

.console-page__section-title:first-of-type {
  margin-top: 8px;
}
</style>
